export type Locale = 'en' | 'de';

export const defaultLocale: Locale = 'en';
export const supportedLocales: Locale[] = ['en', 'de'];

export const translations = {
  en: {
    brand: 'Scalent',
    nav: {
      login: 'Log in',
      register: 'Get started',
      logout: 'Sign out',
      dashboard: 'Projects',
      organizations: 'Organizations',
      framework: 'Framework',
      settings: 'Settings',
      activeProject: 'Active Project',
      newProject: 'New Project',
      newOrganization: 'New Organization',
    },
    landing: {
      badge: 'AI-powered company builder',
      headline: 'From idea to operating company.',
      subheadline:
        'Scalent turns a simple business idea into a complete company setup: brand strategy, legal map, software stack, SOPs, org chart, roles, and a scaling framework. Built for founders who move fast and think long-term.',
      ctaPrimary: 'Start building',
      ctaSecondary: 'I already have an account',
      pricingTitle: 'Choose your pace',
      pricingSubtitle: 'Start free. Upgrade when you are ready to build the full outline for a real company.',
      plans: {
        starter: {
          name: 'Starter',
          price: 'Free',
          description: 'Test the engine with a few AI requests.',
          cta: 'Get started free',
          features: [
            '3 AI-powered requests',
            'Preview one section at a time',
            'Export as Markdown',
            'Paragraph-level replies',
          ],
        },
        founder: {
          name: 'Founder',
          price: '$29',
          period: '/ month',
          description: 'Build the complete outline for one business.',
          cta: 'Start building',
          popular: 'Most popular',
          features: [
            'Unlimited AI requests',
            'Complete company outline',
            'Legal & compliance map',
            'Org chart and role outlines',
            'Full SOP roadmap',
            'Priority generation',
          ],
        },
        scale: {
          name: 'Scale',
          price: '$79',
          period: '/ month',
          description: 'Run multiple businesses or teams.',
          cta: 'Scale up',
          features: [
            'Everything in Founder',
            'Unlimited projects',
            'Multiple company outlines',
            'Team collaboration (coming soon)',
            'Custom templates (coming soon)',
            'Dedicated support',
          ],
        },
      },
      disclaimer: 'Subscription billing and exact feature gating will be finalized before public launch. Prices shown are placeholders.',
      features: {
        fast: {
          title: 'Fast strategic output',
          text: 'Generate a complete business outline in minutes, not weeks.',
        },
        operator: {
          title: 'Built for operators',
          text: 'Legal, finance, HR, and ops guidance—not generic advice.',
        },
        scale: {
          title: 'Scales with you',
          text: 'From first idea to your 50th employee. Add team seats as you grow.',
        },
      },
      footer: 'Built for founders, not teenagers.',
    },
    auth: {
      email: 'Email',
      password: 'Password',
      confirmPassword: 'Confirm password',
      plan: 'Plan',
      createAccount: 'Create account',
      signIn: 'Sign in',
      alreadyAccount: 'Already have an account?',
      noAccount: "Don't have an account?",
      register: 'Register',
      login: 'Login',
      passwordHint: 'Must be at least 8 characters',
      errors: {
        invalidCredentials: 'Invalid email or password',
        emailInUse: 'An account with this email already exists',
        passwordMismatch: 'Passwords do not match',
        weakPassword: 'Password must be at least 8 characters',
      },
    },
    plans: {
      starter: 'Starter',
      founder: 'Founder',
      scale: 'Scale',
    },
    dashboard: {
      title: 'Projects',
      subtitle:
        'Turn a business idea into a complete company setup with AI-generated company guides, SOPs, legal maps, org charts, and scaling plans.',
      emptyTitle: 'No projects yet',
      emptyDescription:
        'Describe your business idea and let Scalent generate your company setup, brand identity, SOPs, legal guidance, org chart, and scaling plans.',
      emptyCta: 'Create Your First Project',
      requests: 'requests',
      deleteConfirm: (name: string) => `Delete "${name}"?`,
      open: 'Open project',
      industry: 'General',
      filterLabel: 'Filter by organization',
      filterAll: 'All projects',
      filterEmpty: 'No projects in this organization yet.',
    },
    project: {
      back: 'Back to projects',
      settings: 'Settings',
      export: 'Export',
      generateAll: 'Generate All',
      generating: 'Generating...',
      regenerate: 'Regenerate',
      locked: 'Locked — upgrade to generate',
      upgradeTitle: 'Upgrade to unlock the complete outline',
      upgradeHint: (sections: string) =>
        `${sections} is included in Founder and Scale plans. Upgrade to unlock the complete outline.`,
      upgradeHintPlural: (sections: string) =>
        `Several sections (${sections}) require a paid plan. Generate the unlocked sections one by one or upgrade to build the complete outline.`,
      idleTitle: (section: string) => `Generate ${section}`,
      idleDescription: 'Use AI to create this section based on your project input and the framework templates.',
      idleCta: 'Generate Now',
      upgradeNow: 'Upgrade to Founder / Scale',
      lockedCta: 'Unlock with Founder / Scale',
      errorTitle: 'Generation failed',
      errorSupport: 'If the problem persists, contact support.',
      linkedOrgsTitle: 'Linked organizations',
      linkedOrgsRail: 'Organizations',
      noLinkedOrgs: 'No organizations linked to this project yet.',
      addOrgCta: 'Add organization',
      addOrgEmpty: 'No organizations to add. Create one from the Organizations tab first.',
      unlinkOrg: 'Unlink',
      unlinkOrgConfirm: (name: string) => `Remove "${name}" from this project?`,
      openOrg: 'Open organization',
    },
    upgrade: {
      title: 'Upgrade your plan',
      subtitle: 'Unlock the complete company outline, unlimited AI requests, and every section.',
      currentPlan: 'Current plan',
      cta: 'Upgrade to {plan}',
      billingNote: 'Subscription billing is not yet connected. Your plan will be updated immediately for testing.',
      successTitle: 'Plan upgraded',
      successBody: (plan: string) => `You are now on the ${plan} plan. Your request limit has been reset and all sections are unlocked.`,
      backToProjects: 'Back to projects',
      errorTitle: 'Upgrade failed',
      limitTitle: 'Free limit reached',
      limitBody: 'Upgrade to Founder or Scale to keep generating sections and unlock the complete company outline.',
      limitCta: 'Upgrade now',
      lockedTitle: 'Complete outline section',
      lockedBody: (section: string) => `${section} is included in Founder and Scale plans. Upgrade to unlock the complete outline.`,
      lockedCta: 'Unlock section',
      blockedTitle: 'Complete outline sections',
      blockedBody: (sections: string) => `Several sections (${sections}) require a paid plan. Upgrade to unlock the complete outline.`,
    },
    admin: {
      title: 'Admin',
      subtitle: 'Manage accounts, plans, roles and usage.',
      signedInAs: 'Signed in as',
      loading: 'Loading accounts...',
      empty: 'No accounts found.',
      refresh: 'Refresh',
      email: 'Email',
      plan: 'Plan',
      role: 'Role',
      usage: 'Usage',
      created: 'Created',
      actions: 'Actions',
      userRole: 'User',
      adminRole: 'Admin',
      resetUsage: 'Reset usage',
      delete: 'Delete account',
      deleteConfirm: (email: string) => `Delete account "${email}"? This cannot be undone.`,
      accessDenied: 'Access denied',
      adminOnly: 'This page is only available to administrators.',
      errorLoad: 'Failed to load accounts.',
      errorSave: 'Failed to save changes.',
      errorDelete: 'Failed to delete account.',
    },
    settings: {
      title: 'Account & Settings',
      currentPlan: 'Current Plan',
      aiRequests: 'AI requests this period',
      aiStatus: 'AI Service Status',
      aiDescription: 'The AI backend is configured by the site administrator. You can test that it is responding below.',
      testAi: 'Test AI Connection',
      testing: 'Testing...',
      appearance: 'Appearance',
      darkMode: 'Dark mode (placeholder)',
      planLabels: {
        starter: 'Starter (Free)',
        founder: 'Founder',
        scale: 'Scale',
      },
      planFeatures: {
        starter: ['3 AI requests', 'Company setup, brand identity, software stack', 'Preview the framework before subscribing'],
        paid: [
          'Generate the complete outline',
          'SOPs, legal compliance, org chart, scaling',
          'Export every section as a sub-document',
        ],
      },
    },
    newProject: {
      title: 'Create New Project',
      subtitle: 'Describe your idea. You can generate each section from the workspace.',
      name: 'Project Name',
      namePlaceholder: 'e.g., Nova Analytics',
      idea: 'Business Idea',
      ideaPlaceholder: 'Describe the product, service, target customer, and problem you solve...',
      industry: 'Industry',
      industryPlaceholder: 'e.g., SaaS, Healthcare, Retail',
      location: 'Primary Location',
      locationPlaceholder: 'e.g., Delaware, USA',
      teamSize: 'Team Size / Stage',
      teamSizePlaceholder: 'e.g., 2 founders',
      stage: 'Business Stage',
      stageOptions: {
        '': 'Select stage...',
        idea: 'Idea / Concept',
        mvp: 'MVP Development',
        early: 'Early Revenue',
        growth: 'Growth / Scaling',
      },
      goals: 'Goals',
      goalsPlaceholder: 'What do you want to achieve in the next 6–12 months?',
      required: 'Required',
      cancel: 'Cancel',
      create: 'Create Project',
      language: 'Project Language',
      languageHelp: 'Choose the language for AI-generated sections. You can still build a company anywhere in the world.',
      languageLabel: 'Language',
      errors: {
        nameRequired: 'Project name is required',
        ideaRequired: 'Business idea is required',
      },
    },
    framework: {
      title: 'Framework Reference',
      searchPlaceholder: 'Find section...',
      empty: 'Select a document from the sidebar to view the framework content.',
    },
    document: {
      outline: 'Document Outline',
      sections: (count: number) => `${count} section${count !== 1 ? 's' : ''}`,
      blocks: (count: number) => `${count} total blocks`,
      exportAll: 'Export all sections',
      copyMarkdown: 'Copy Markdown',
      copyHtml: 'Copy HTML',
      export: 'Export',
      focus: 'Focus view',
      collapse: 'Collapse section',
      expand: 'Expand section',
      showSource: 'Show Markdown source',
      hideSource: 'Hide Markdown source',
      reply: 'Reply',
      replyTitle: 'Reply to paragraph',
      selectedText: 'Selected text',
      previousReplies: 'Previous replies',
      latestReply: 'Latest AI reply',
      replyPlaceholder: 'Ask a question or request an adaptation... (e.g., make this shorter, more detailed, or tailored to healthcare)',
      send: 'Send reply',
      thinking: 'Thinking...',
      shortcut: 'Cmd/Ctrl + Enter to send',
    },
    language: {
      label: 'Language',
      en: 'English',
      de: 'German',
    },
    errors: {
      authRequired: 'Authentication required',
      planLimit: 'Free limit reached. Upgrade to Founder or Scale to keep building.',
      upgradeRequired: 'Complete outline sections require a Founder or Scale subscription.',
    },
    interview: {
      // Intakes and entry points
      intakeTitle: 'Start a new company project',
      intakeSubtitle: 'We will work out the idea, the problem, the solution, and the goals together in a short Q&A. The result becomes the seed for your full company build-out.',
      organizationIntakeSubtitle: 'A focused Q&A about a company, supplier, or partner. The result becomes a structured organization you can link to any project.',
      intakeName: 'Project name',
      intakeNamePlaceholder: 'e.g. Nova Analytics',
      intakeProblem: 'Problem Definition',
      intakeProblemHelp: 'What is the problem you want to solve, and who feels it?',
      intakeProblemPlaceholder: 'e.g. Small businesses waste hours every week reformatting spreadsheets between their bank, accounting, and reporting tools.',
      intakeProjectIdea: 'Project Idea',
      intakeProjectIdeaHelp: 'What is the proposed solution? You can fill in just this, just the problem, or both.',
      intakeProjectIdeaPlaceholder: 'e.g. A small script that pulls from the bank and writes to the other two in the format each tool expects.',
      intakeIdea: 'Business idea (one line)',
      intakeIdeaPlaceholder: 'What is this project, in one sentence?',
      intakeLanguage: 'Interview language',
      intakeLanguageHelp: 'The interview and the document it produces will be in this language.',
      intakeStart: 'Begin interview',
      intakeCancel: 'Cancel',
      intakeErrors: {
        nameRequired: 'Project name is required',
        ideaRequired: 'Business idea is required',
        atLeastOneRequired: 'Add a problem definition or a project idea (or both).',
      },
      // Session UI
      sessionTitle: 'Interview',
      sessionSubtitle: 'Answer one question at a time. The document and the goals list grow as you go.',
      sessionEnd: 'Finish',
      sessionEndConfirm: 'Mark this interview complete? The document and goals will be saved.',
      sessionAbandon: 'Abandon',
      sessionAbandonConfirm: 'Abandon this interview? The document will be kept but marked abandoned.',
      directionLabel: 'Direction (steering note)',
      directionHelp: 'Optional. Tell the engine what to focus on, what to avoid, or what tone to take. Edits apply to the next question.',
      directionPlaceholder: 'e.g. focus on European B2B SaaS, lean operations, no agencies.',
      // Document and extract
      documentTitle: 'Working document',
      documentEmpty: 'The document will grow here. Each answer fills in the next section.',
      extractTitle: 'Structured extract',
      extractTopic: 'Topic',
      extractPurpose: 'Purpose',
      extractProblem: 'Problem',
      extractSolution: 'Solution',
      extractScope: 'Scope',
      extractGoals: 'Goals',
      extractRisks: 'Risks',
      extractNextSteps: 'Next steps',
      extractNoGoals: 'No goals yet — the engine will propose some as the interview advances.',
      transcriptTitle: 'Full transcript',
      transcriptEmpty: 'No questions asked yet.',
      // Question card
      questionEmpty: 'Loading the first question...',
      questionStuck: 'The interview was interrupted before the next question was generated.',
      questionContinue: 'Continue interview',
      questionGenerating: 'Generating the next question…',
      questionFailed: 'Question unavailable. Try again.',
      questionRetry: 'Try again',
      answerPlaceholder: 'Type your answer. Be as specific as you can.',
      answerSend: 'Send',
      answerSending: 'Sending...',
      // Controls
      clarify: 'Clarify question',
      clarifyClose: 'Hide clarification',
      skip: 'Skip',
      redo: 'Redo document',
      redoConfirm: 'Regenerate the document and goals from the current transcript? The transcript will not change.',
      redoRunning: 'Regenerating...',
      // Status banners
      updateFailed: 'Your answer was saved, but the document update failed. Try again — your answer is safe.',
      nextFailed: 'Question generation failed. Try again.',
      clarifyFailed: 'Clarification could not be generated. Try again.',
      // Project-level advance
      advanceTitle: 'Advance this project',
      advanceSubtitle: 'A focused Q&A to sharpen scope, add new goals, or challenge existing assumptions.',
      advanceStart: 'Start advance interview',
      // Project auto-migration prompt
      migrationTitle: 'Welcome back — your old projects are waiting',
      migrationSubtitle: 'We can re-open each existing project as an advance interview so you can fill in the idea, problem, and solution properly. New projects always start with the Q&A.',
      migrationRunAll: 'Open all in sequence',
      migrationRunOne: 'Open this one',
      migrationSkip: 'Skip for now',
      // Mobile tab labels
      tabQuestion: 'Question',
      tabDocument: 'Document',
      tabExtract: 'Extract',
      openTranscriptHint: 'Show transcript',
    },
    org: {
      title: 'Organizations',
      subtitle:
        'Companies generated from your interviews. Link them to projects as supporting documents.',
      newCta: 'New Organization',
      emptyTitle: 'No organizations yet',
      emptyDescription:
        'Start a focused Q&A about a company, supplier, or partner. The result becomes a structured organization you can link to any project.',
      emptyCta: 'Create Your First Organization',
      deleteConfirm: (name: string) => `Delete organization "${name}"?`,
      open: 'Open organization',
      back: 'Back to dashboard',
      notFoundTitle: 'Organization not found',
      notFoundBody: 'It may have been deleted. Open the organizations list to pick another.',
      backToList: 'Back to organizations',
      documentTitle: 'Working document',
      documentEmpty: 'The document will grow here. Each interview answer fills in the next section.',
      extractTitle: 'Structured extract',
      linkedProjects: 'Linked projects',
      noLinkedProjects: 'No projects are linked to this organization yet.',
      linkedProjectSingular: 'linked project',
      linkedProjectPlural: 'linked projects',
      openProject: 'Open project',
      addProjectCta: 'Add project',
      addProjectEmpty: 'No projects to add. Create one from the Projects tab first.',
      unlinkProject: 'Unlink',
      unlinkProjectConfirm: (name: string) => `Remove "${name}" from this organization?`,
      industryFallback: 'General',
      createFromOrgCta: 'New interview',
      createFromProjectCta: 'Create organization from this project',
      export: 'Export',
      docsLabel: 'documents',
      docsRailHeader: 'Generated framework documents',
      docPending: 'Pending — will be generated next.',
      docEmpty: 'This document has not been generated yet.',
      generatingDoc: 'Generating this document from the interview and the framework…',
      errorDoc: 'Generation failed. Use Regenerate to try again.',
      regenerate: 'Regenerate',
      exportDoc: 'Export',
      generateAll: 'Generate all documents',
    },
  },

  de: {
    brand: 'Scalent',
    nav: {
      login: 'Anmelden',
      register: 'Loslegen',
      logout: 'Abmelden',
      dashboard: 'Projekte',
      organizations: 'Organisationen',
      framework: 'Framework',
      settings: 'Einstellungen',
      activeProject: 'Aktives Projekt',
      newProject: 'Neues Projekt',
      newOrganization: 'Neue Organisation',
    },
    landing: {
      badge: 'KI-gestützter Company Builder',
      headline: 'Von der Idee zum operierenden Unternehmen.',
      subheadline:
        'Scalent verwandelt eine einfache Geschäftsidee in einen kompletten Firmenaufbau: Markenstrategie, rechtliche Struktur, Software-Stack, SOPs, Organigramm, Rollen und einen Scaling-Rahmen. Für Gründer, die schnell handeln und langfristig denken.',
      ctaPrimary: 'Jetzt aufbauen',
      ctaSecondary: 'Ich habe bereits ein Konto',
      pricingTitle: 'Wähle dein Tempo',
      pricingSubtitle: 'Starte kostenlos. Upgrade, wenn du den vollständigen Plan für ein reales Unternehmen erstellen willst.',
      plans: {
        starter: {
          name: 'Starter',
          price: 'Kostenlos',
          description: 'Teste die Engine mit einigen KI-Anfragen.',
          cta: 'Kostenlos starten',
          features: [
            '3 KI-gestützte Anfragen',
            'Vorschau eines Abschnitts nach dem anderen',
            'Export als Markdown',
            'Antworten auf Absatzebene',
          ],
        },
        founder: {
          name: 'Founder',
          price: '29 €',
          period: '/ Monat',
          description: 'Erstelle den vollständigen Plan für ein Unternehmen.',
          cta: 'Jetzt aufbauen',
          popular: 'Beliebteste Wahl',
          features: [
            'Unbegrenzte KI-Anfragen',
            'Vollständiger Unternehmensplan',
            'Rechts- & Compliance-Übersicht',
            'Organigramm und Rollenprofile',
            'Vollständige SOP-Roadmap',
            'Priorisierte Generierung',
          ],
        },
        scale: {
          name: 'Scale',
          price: '79 €',
          period: '/ Monat',
          description: 'Führe mehrere Unternehmen oder Teams.',
          cta: 'Skalieren',
          features: [
            'Alles aus Founder',
            'Unbegrenzte Projekte',
            'Mehrere Unternehmenspläne',
            'Team-Zusammenarbeit (demnächst)',
            'Eigene Vorlagen (demnächst)',
            'Persönlicher Support',
          ],
        },
      },
      disclaimer: 'Abrechnung und genaue Feature-Aufteilung werden vor dem öffentlichen Launch finalisiert. Preise sind Platzhalter.',
      features: {
        fast: {
          title: 'Schneller strategischer Output',
          text: 'Generiere einen vollständigen Unternehmensplan in Minuten, nicht Wochen.',
        },
        operator: {
          title: 'Für Operator gebaut',
          text: 'Recht, Finanzen, HR und Operations – keine generischen Ratschläge.',
        },
        scale: {
          title: 'Wächst mit dir',
          text: 'Von der ersten Idee bis zum 50. Mitarbeiter. Team-Plätze nach Bedarf hinzufügen.',
        },
      },
      footer: 'Für Gründer, nicht für Teenager.',
    },
    auth: {
      email: 'E-Mail',
      password: 'Passwort',
      confirmPassword: 'Passwort bestätigen',
      plan: 'Plan',
      createAccount: 'Konto erstellen',
      signIn: 'Anmelden',
      alreadyAccount: 'Bereits ein Konto?',
      noAccount: 'Noch kein Konto?',
      register: 'Registrieren',
      login: 'Anmeldung',
      passwordHint: 'Mindestens 8 Zeichen',
      errors: {
        invalidCredentials: 'Ungültige E-Mail oder Passwort',
        emailInUse: 'Ein Konto mit dieser E-Mail existiert bereits',
        passwordMismatch: 'Passwörter stimmen nicht überein',
        weakPassword: 'Passwort muss mindestens 8 Zeichen haben',
      },
    },
    plans: {
      starter: 'Starter',
      founder: 'Founder',
      scale: 'Scale',
    },
    dashboard: {
      title: 'Projekte',
      subtitle:
        'Verwandle eine Geschäftsidee in einen kompletten Firmenaufbau mit KI-generierten Guides, SOPs, Rechts-Übersichten, Organigrammen und Scaling-Plänen.',
      emptyTitle: 'Noch keine Projekte',
      emptyDescription:
        'Beschreibe deine Geschäftsidee und Scalent erstellt Firmenaufbau, Markenidentität, SOPs, rechtliche Hinweise, Organigramm und Scaling-Plan.',
      emptyCta: 'Erstelle dein erstes Projekt',
      requests: 'Anfragen',
      deleteConfirm: (name: string) => `"${name}" löschen?`,
      open: 'Projekt öffnen',
      industry: 'Allgemein',
      filterLabel: 'Nach Organisation filtern',
      filterAll: 'Alle Projekte',
      filterEmpty: 'Noch keine Projekte in dieser Organisation.',
    },
    project: {
      back: 'Zurück zu Projekten',
      settings: 'Einstellungen',
      export: 'Exportieren',
      generateAll: 'Alles generieren',
      generating: 'Generiere...',
      regenerate: 'Neu generieren',
      locked: 'Gesperrt — Upgrade erforderlich',
      upgradeTitle: 'Upgrade für den vollständigen Plan',
      upgradeHint: (sections: string) =>
        `${sections} ist in den Founder- und Scale-Plänen enthalten. Upgrade, um den vollständigen Plan freizuschalten.`,
      upgradeHintPlural: (sections: string) =>
        `Mehrere Abschnitte (${sections}) erfordern einen kostenpflichtigen Plan. Generiere die freigeschalteten Abschnitte einzeln oder upgrade für den vollständigen Plan.`,
      idleTitle: (section: string) => `${section} generieren`,
      idleDescription: 'Nutze KI, um diesen Abschnitt basierend auf deinen Projektdaten und den Framework-Vorlagen zu erstellen.',
      idleCta: 'Jetzt generieren',
      upgradeNow: 'Upgrade auf Founder / Scale',
      lockedCta: 'Mit Founder / Scale freischalten',
      errorTitle: 'Generierung fehlgeschlagen',
      errorSupport: 'Wenn das Problem weiterhin besteht, kontaktiere den Support.',
      linkedOrgsTitle: 'Verknüpfte Organisationen',
      linkedOrgsRail: 'Organisationen',
      noLinkedOrgs: 'Noch keine Organisationen mit diesem Projekt verknüpft.',
      addOrgCta: 'Organisation hinzufügen',
      addOrgEmpty: 'Keine Organisationen zum Hinzufügen. Lege zuerst eine im Organisations-Tab an.',
      unlinkOrg: 'Verknüpfung aufheben',
      unlinkOrgConfirm: (name: string) => `"${name}" von diesem Projekt entfernen?`,
      openOrg: 'Organisation öffnen',
    },
    upgrade: {
      title: 'Plan upgraden',
      subtitle: 'Schalte den vollständigen Unternehmensplan, unbegrenzte KI-Anfragen und alle Abschnitte frei.',
      currentPlan: 'Aktueller Plan',
      cta: 'Upgrade auf {plan}',
      billingNote: 'Die Abrechnung ist noch nicht angebunden. Dein Plan wird sofort zum Testen aktualisiert.',
      successTitle: 'Plan aktualisiert',
      successBody: (plan: string) => `Du bist jetzt im ${plan}-Plan. Dein Anfragen-Limit wurde zurückgesetzt und alle Abschnitte sind freigeschaltet.`,
      backToProjects: 'Zurück zu Projekten',
      errorTitle: 'Upgrade fehlgeschlagen',
      limitTitle: 'Kostenloses Limit erreicht',
      limitBody: 'Upgrade auf Founder oder Scale, um weiter Abschnitte zu generieren und den vollständigen Unternehmensplan freizuschalten.',
      limitCta: 'Jetzt upgraden',
      lockedTitle: 'Abschnitt des vollständigen Plans',
      lockedBody: (section: string) => `${section} ist in den Founder- und Scale-Plänen enthalten. Upgrade, um den vollständigen Plan freizuschalten.`,
      lockedCta: 'Abschnitt freischalten',
      blockedTitle: 'Abschnitte des vollständigen Plans',
      blockedBody: (sections: string) => `Mehrere Abschnitte (${sections}) erfordern einen kostenpflichtigen Plan. Upgrade, um den vollständigen Plan freizuschalten.`,
    },
    admin: {
      title: 'Admin',
      subtitle: 'Konten, Pläne, Rollen und Nutzung verwalten.',
      signedInAs: 'Angemeldet als',
      loading: 'Konten werden geladen...',
      empty: 'Keine Konten gefunden.',
      refresh: 'Aktualisieren',
      email: 'E-Mail',
      plan: 'Plan',
      role: 'Rolle',
      usage: 'Nutzung',
      created: 'Erstellt',
      actions: 'Aktionen',
      userRole: 'User',
      adminRole: 'Admin',
      resetUsage: 'Nutzung zurücksetzen',
      delete: 'Konto löschen',
      deleteConfirm: (email: string) => `Konto "${email}" löschen? Dies kann nicht rückgängig gemacht werden.`,
      accessDenied: 'Zugriff verweigert',
      adminOnly: 'Diese Seite ist nur für Administratoren verfügbar.',
      errorLoad: 'Konten konnten nicht geladen werden.',
      errorSave: 'Änderungen konnten nicht gespeichert werden.',
      errorDelete: 'Konto konnte nicht gelöscht werden.',
    },
    settings: {
      title: 'Konto & Einstellungen',
      currentPlan: 'Aktueller Plan',
      aiRequests: 'KI-Anfragen in diesem Zeitraum',
      aiStatus: 'KI-Service-Status',
      aiDescription: 'Das KI-Backend wird vom Administrator konfiguriert. Teste hier, ob es antwortet.',
      testAi: 'KI-Verbindung testen',
      testing: 'Teste...',
      appearance: 'Erscheinungsbild',
      darkMode: 'Dark Mode (Platzhalter)',
      planLabels: {
        starter: 'Starter (Kostenlos)',
        founder: 'Founder',
        scale: 'Scale',
      },
      planFeatures: {
        starter: ['3 KI-Anfragen', 'Firmenaufbau, Markenidentität, Software-Stack', 'Framework vor dem Abo testen'],
        paid: [
          'Vollständigen Plan generieren',
          'SOPs, Rechts-Compliance, Organigramm, Scaling',
          'Jeden Abschnitt als Unterdokument exportieren',
        ],
      },
    },
    newProject: {
      title: 'Neues Projekt erstellen',
      subtitle: 'Beschreibe deine Idee. Du kannst jeden Abschnitt im Arbeitsbereich generieren.',
      name: 'Projektname',
      namePlaceholder: 'z. B. Nova Analytics',
      idea: 'Geschäftsidee',
      ideaPlaceholder: 'Beschreibe Produkt, Service, Zielkunde und das gelöste Problem...',
      industry: 'Branche',
      industryPlaceholder: 'z. B. SaaS, Healthcare, Retail',
      location: 'Hauptsitz',
      locationPlaceholder: 'z. B. Delaware, USA',
      teamSize: 'Teamgröße / Phase',
      teamSizePlaceholder: 'z. B. 2 Gründer',
      stage: 'Unternehmensphase',
      stageOptions: {
        '': 'Phase wählen...',
        idea: 'Idee / Konzept',
        mvp: 'MVP-Entwicklung',
        early: 'Erste Einnahmen',
        growth: 'Wachstum / Skalierung',
      },
      goals: 'Ziele',
      goalsPlaceholder: 'Was willst du in den nächsten 6–12 Monaten erreichen?',
      required: 'Erforderlich',
      cancel: 'Abbrechen',
      create: 'Projekt erstellen',
      language: 'Projektsprache',
      languageHelp: 'Wähle die Sprache für KI-generierte Abschnitte. Du kannst trotzdem ein Unternehmen in jedem Land der Welt aufbauen.',
      languageLabel: 'Sprache',
      errors: {
        nameRequired: 'Projektname ist erforderlich',
        ideaRequired: 'Geschäftsidee ist erforderlich',
      },
    },
    framework: {
      title: 'Framework-Referenz',
      searchPlaceholder: 'Abschnitt suchen...',
      empty: 'Wähle ein Dokument in der Seitenleiste, um den Framework-Inhalt anzuzeigen.',
    },
    document: {
      outline: 'Dokument-Gliederung',
      sections: (count: number) => `${count} Abschnitt${count !== 1 ? 'e' : ''}`,
      blocks: (count: number) => `${count} Blöcke insgesamt`,
      exportAll: 'Alle Abschnitte exportieren',
      copyMarkdown: 'Markdown kopieren',
      copyHtml: 'HTML kopieren',
      export: 'Exportieren',
      focus: 'Fokusansicht',
      collapse: 'Abschnitt einklappen',
      expand: 'Abschnitt ausklappen',
      showSource: 'Markdown-Quelle anzeigen',
      hideSource: 'Markdown-Quelle ausblenden',
      reply: 'Antworten',
      replyTitle: 'Auf Absatz antworten',
      selectedText: 'Ausgewählter Text',
      previousReplies: 'Vorherige Antworten',
      latestReply: 'Neueste KI-Antwort',
      replyPlaceholder: 'Stelle eine Frage oder passe den Inhalt an... (z. B. kürzer, detaillierter oder auf Healthcare zugeschnitten)',
      send: 'Antwort senden',
      thinking: 'Denkt nach...',
      shortcut: 'Cmd/Ctrl + Enter zum Senden',
    },
    language: {
      label: 'Sprache',
      en: 'Englisch',
      de: 'Deutsch',
    },
    errors: {
      authRequired: 'Anmeldung erforderlich',
      planLimit: 'Kostenloses Limit erreicht. Upgrade auf Founder oder Scale, um weiterzubauen.',
      upgradeRequired: 'Vollständige Plan-Abschnitte erfordern ein Founder- oder Scale-Abonnement.',
    },
    interview: {
      intakeTitle: 'Neues Unternehmensprojekt starten',
      intakeSubtitle: 'Wir arbeiten Idee, Problem, Lösung und Ziele in einer kurzen Q&A gemeinsam heraus. Das Ergebnis wird der Ausgangspunkt für den vollständigen Firmenaufbau.',
      organizationIntakeSubtitle: 'Eine fokussierte Q&A zu einem Unternehmen, Lieferanten oder Partner. Das Ergebnis wird eine strukturierte Organisation, die du mit jedem Projekt verknüpfen kannst.',
      intakeName: 'Projektname',
      intakeNamePlaceholder: 'z. B. Nova Analytics',
      intakeProblem: 'Problemdefinition',
      intakeProblemHelp: 'Welches Problem willst du lösen, und wer spürt es?',
      intakeProblemPlaceholder: 'z. B. Kleinunternehmen verschwenden jede Woche Stunden damit, Tabellen zwischen Bank, Buchhaltung und Reporting-Tools umzuformatieren.',
      intakeProjectIdea: 'Projektidee',
      intakeProjectIdeaHelp: 'Was ist der vorgeschlagene Lösungsansatz? Du kannst nur dieses Feld, nur das Problem oder beides ausfüllen.',
      intakeProjectIdeaPlaceholder: 'z. B. Ein kleines Skript, das Daten von der Bank holt und in das erwartete Format der anderen beiden Tools schreibt.',
      intakeIdea: 'Geschäftsidee (eine Zeile)',
      intakeIdeaPlaceholder: 'Worum geht es bei diesem Projekt, in einem Satz?',
      intakeLanguage: 'Interview-Sprache',
      intakeLanguageHelp: 'Das Interview und das erzeugte Dokument werden in dieser Sprache sein.',
      intakeStart: 'Interview starten',
      intakeCancel: 'Abbrechen',
      intakeErrors: {
        nameRequired: 'Projektname ist erforderlich',
        ideaRequired: 'Geschäftsidee ist erforderlich',
        atLeastOneRequired: 'Füge eine Problemdefinition oder eine Projektidee hinzu (oder beides).',
      },
      sessionTitle: 'Interview',
      sessionSubtitle: 'Beantworte eine Frage nach der anderen. Dokument und Zielliste wachsen mit.',
      sessionEnd: 'Abschließen',
      sessionEndConfirm: 'Dieses Interview als abgeschlossen markieren? Dokument und Ziele werden gespeichert.',
      sessionAbandon: 'Verwerfen',
      sessionAbandonConfirm: 'Dieses Interview verwerfen? Das Dokument bleibt erhalten, wird aber als verworfen markiert.',
      directionLabel: 'Richtung (Steuerungsnotiz)',
      directionHelp: 'Optional. Sag der Engine, worauf sie sich konzentrieren soll, was sie vermeiden soll, welcher Ton gewünscht ist. Änderungen gelten ab der nächsten Frage.',
      directionPlaceholder: 'z. B. Fokus auf europäisches B2B-SaaS, schlanke Operations, keine Agenturen.',
      documentTitle: 'Arbeitsdokument',
      documentEmpty: 'Hier wächst das Dokument. Jede Antwort füllt den nächsten Abschnitt.',
      extractTitle: 'Strukturierter Extrakt',
      extractTopic: 'Thema',
      extractPurpose: 'Zweck',
      extractProblem: 'Problem',
      extractSolution: 'Lösung',
      extractScope: 'Umfang',
      extractGoals: 'Ziele',
      extractRisks: 'Risiken',
      extractNextSteps: 'Nächste Schritte',
      extractNoGoals: 'Noch keine Ziele — die Engine schlägt welche vor, während das Interview voranschreitet.',
      transcriptTitle: 'Vollständiges Transkript',
      transcriptEmpty: 'Noch keine Fragen gestellt.',
      questionEmpty: 'Lade die erste Frage...',
      questionStuck: 'Das Interview wurde unterbrochen, bevor die nächste Frage erzeugt wurde.',
      questionContinue: 'Interview fortsetzen',
      questionGenerating: 'Erzeuge die nächste Frage…',
      questionFailed: 'Frage nicht verfügbar. Bitte erneut versuchen.',
      questionRetry: 'Erneut versuchen',
      answerPlaceholder: 'Antwort hier eingeben. So spezifisch wie möglich.',
      answerSend: 'Senden',
      answerSending: 'Sendet...',
      clarify: 'Frage verdeutlichen',
      clarifyClose: 'Verdeutlichung ausblenden',
      skip: 'Überspringen',
      redo: 'Dokument neu erstellen',
      redoConfirm: 'Dokument und Ziele aus dem aktuellen Transkript neu generieren? Das Transkript bleibt unverändert.',
      redoRunning: 'Regeneriere...',
      updateFailed: 'Deine Antwort wurde gespeichert, aber das Dokument-Update ist fehlgeschlagen. Erneut versuchen — deine Antwort ist sicher.',
      nextFailed: 'Frage-Generierung fehlgeschlagen. Erneut versuchen.',
      clarifyFailed: 'Verdeutlichung konnte nicht generiert werden. Erneut versuchen.',
      advanceTitle: 'Projekt vertiefen',
      advanceSubtitle: 'Eine fokussierte Q&A, um den Umfang zu schärfen, neue Ziele hinzuzufügen oder bestehende Annahmen zu hinterfragen.',
      advanceStart: 'Vertiefungs-Interview starten',
      migrationTitle: 'Willkommen zurück — deine alten Projekte warten',
      migrationSubtitle: 'Wir können jedes bestehende Projekt als Vertiefungs-Interview wieder öffnen, damit du Idee, Problem und Lösung sauber ausarbeiten kannst. Neue Projekte starten immer mit der Q&A.',
      migrationRunAll: 'Alle nacheinander öffnen',
      migrationRunOne: 'Dieses öffnen',
      migrationSkip: 'Später',
      // Mobile tab labels
      tabQuestion: 'Frage',
      tabDocument: 'Dokument',
      tabExtract: 'Extrakt',
      openTranscriptHint: 'Transkript anzeigen',
    },
    org: {
      title: 'Organisationen',
      subtitle:
        'Unternehmen aus deinen Interviews. Verknüpfe sie mit Projekten als unterstützende Dokumente.',
      newCta: 'Neue Organisation',
      emptyTitle: 'Noch keine Organisationen',
      emptyDescription:
        'Starte eine fokussierte Q&A zu einem Unternehmen, Lieferanten oder Partner. Das Ergebnis wird eine strukturierte Organisation, die du mit jedem Projekt verknüpfen kannst.',
      emptyCta: 'Erstelle deine erste Organisation',
      deleteConfirm: (name: string) => `Organisation "${name}" löschen?`,
      open: 'Organisation öffnen',
      back: 'Zurück zum Dashboard',
      notFoundTitle: 'Organisation nicht gefunden',
      notFoundBody: 'Sie wurde möglicherweise gelöscht. Öffne die Organisationsliste, um eine andere zu wählen.',
      backToList: 'Zurück zu den Organisationen',
      documentTitle: 'Arbeitsdokument',
      documentEmpty: 'Hier wächst das Dokument. Jede Interview-Antwort füllt den nächsten Abschnitt.',
      extractTitle: 'Strukturierter Extrakt',
      linkedProjects: 'Verknüpfte Projekte',
      noLinkedProjects: 'Noch keine Projekte mit dieser Organisation verknüpft.',
      linkedProjectSingular: 'verknüpftes Projekt',
      linkedProjectPlural: 'verknüpfte Projekte',
      openProject: 'Projekt öffnen',
      addProjectCta: 'Projekt hinzufügen',
      addProjectEmpty: 'Keine Projekte zum Hinzufügen. Lege zuerst eines im Projekte-Tab an.',
      unlinkProject: 'Verknüpfung aufheben',
      unlinkProjectConfirm: (name: string) => `"${name}" von dieser Organisation entfernen?`,
      industryFallback: 'Allgemein',
      createFromOrgCta: 'Neues Interview',
      createFromProjectCta: 'Organisation aus diesem Projekt erstellen',
      export: 'Exportieren',
      docsLabel: 'Dokumente',
      docsRailHeader: 'Generierte Framework-Dokumente',
      docPending: 'Wartet — wird als Nächstes erzeugt.',
      docEmpty: 'Dieses Dokument wurde noch nicht erzeugt.',
      generatingDoc: 'Erzeuge dieses Dokument aus dem Interview und dem Framework…',
      errorDoc: 'Erzeugung fehlgeschlagen. Mit Neu erzeugen erneut versuchen.',
      regenerate: 'Neu erzeugen',
      exportDoc: 'Exportieren',
      generateAll: 'Alle Dokumente erzeugen',
    },
  },
} as const;

export type Translations = (typeof translations)['en'] | (typeof translations)['de'];
export type TranslationKey = keyof (typeof translations)['en'];
